/* Signature capture pad — canvas the author draws on with mouse or
 * finger. Emits a data URL PNG when the user clicks "Use signature".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Eraser, Check } from 'lucide-react';

export default function SignatureCanvas({ onCapture }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
    setHasInk(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const stop = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
  };
  const capture = () => {
    const url = canvasRef.current.toDataURL('image/png');
    onCapture?.(url);
  };

  return (
    <div className="space-y-2" data-testid="signature-canvas">
      <canvas
        ref={canvasRef}
        width={480}
        height={140}
        className="w-full max-w-[480px] cursor-crosshair rounded-md border-2 border-dashed border-border bg-white"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={stop}
        data-testid="signature-canvas-pad"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={clear}
          data-testid="signature-clear"
          className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary"
        >
          <Eraser className="h-3 w-3" /> Clear
        </button>
        <button
          type="button"
          onClick={capture}
          disabled={!hasInk}
          data-testid="signature-use"
          className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          <Check className="h-3 w-3" /> Use this signature
        </button>
      </div>
    </div>
  );
}
